import React, { useEffect, createRef, useState } from "react";
import { useTheme } from "react-native-paper";
import { Text, InputAccessoryNavigation } from "../../components";
import { ThemeType } from "../../theme/theme";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StatusBar,
  StyleSheet,
  View,
} from "react-native";
import { Stack } from "../../components/layout/stack";
import { CognitoErrors, UserFields } from "../../enums";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { forgotPasswordSchema } from "../home/schemas";
import { Auth } from "aws-amplify";
import { useNavigation } from "@react-navigation/native";
import { translate } from "../../i18n/translate";
import { AuthService } from "../../api/AuthService";
import { theme as themeUiComponent, TextInput, Button, typography } from "ui-components";
import { domusoResponsiveStyleHelper } from "../new/utils/domuso-responsive";
import { testProps } from "../../utils/testProps";
import { Grid, Container, Row, Column } from "ui-grid";
import { DomusoIcon } from "../../../assets/domuso-icon";
import { Footer } from "../../components/new/footer/footer";

const NOT_ERROR = "not error";

export const ForgotPassword = () => {
  const theme = useTheme() as ThemeType;
  const styles = getStyles(theme);
  const [isLoading, setLoading] = useState(false);
  const inputAccessoryViewID = "inputAccessoryViewID1";
  const ref = [createRef()];
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const navigation = useNavigation();
  const authService = new AuthService();

  const getCognitoErrorMessage = (err: NodeJS.ErrnoException): string => {
    const { USER_NOT_FOUND, NOT_AUTHORIZED, USER_LAMBDA_VALIDATION } = CognitoErrors;
    const { code, message: defaultMessage = "" } = err;
    switch (code) {
      case USER_NOT_FOUND:
        return translate("authStack.noAccountAssociated") || "";
      case NOT_AUTHORIZED:
      case USER_LAMBDA_VALIDATION:
        return NOT_ERROR;
      default:
        return defaultMessage;
    }
  };

  const getDefaultCognitoErrorMessage = (err: NodeJS.ErrnoException): string => {
    const { code, message: defaultMessage = "" } = err;
    return defaultMessage;
  };

  const {
    register,
    trigger,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
  });

  useEffect(() => {
    register(UserFields.EMAIL);
  }, [register]);

  const handleFieldUpdate = async (field: string, val: string) => {
    val = val.trim();
    setValue(field, val);
    await trigger(field);
  };

  const handleForgotPassword = async () => {
    const { email } = getValues();
    try {
      setLoading(true);
      await Auth.forgotPassword(email);
      navigation.navigate("ActionRequired", { email });
    } catch (error) {
      const errorMsg = getCognitoErrorMessage(error);
      if (errorMsg === NOT_ERROR) {
        const response = await authService.sendInvite(email);
        if (response.status >= 400 && response.status < 600) {
          const defErrorMsg = getDefaultCognitoErrorMessage(error);
          navigation.navigate("ActionRequired", { email, defErrorMsg });
        }
        navigation.navigate("ActionRequired", { email });
      } else {
        navigation.navigate("ActionRequired", { email, errorMsg });
      }
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={0}>
      <ScrollView
        showsVerticalScrollIndicator={false}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollableArea}
        keyboardShouldPersistTaps="handled">
        <StatusBar
          hidden={false}
          translucent={false}
          backgroundColor={themeUiComponent.colors.white}
          barStyle={"dark-content"}
        />
        <Grid>
          <Container>
            <Row>
              <Column span={{ xs: false, sm: 1, md: 2, lg: 3 }} />
              <Column span={{ xs: 12, sm: 10, md: 8, lg: 6 }}>
                <View>
                  <View style={{ marginVertical: 15 }}></View>
                  <Stack space="small">
                    <View style={{ alignItems: "center" }}>
                      <DomusoIcon height={45} width={45} color={themeUiComponent.colors.primary} />
                      <View style={{ marginTop: 10 }}>
                        <Text
                          style={styles.heading}
                          text={translate("authStack.forgotPassword2") as string}
                        />
                      </View>
                      <View style={{ marginVertical: 30 }}>
                        <Text style={styles.subHeading} tx="authStack.enterEmail" />
                      </View>
                    </View>
                  </Stack>
                </View>
              </Column>
              <Column span={{ xs: false, sm: 1, md: 2, lg: 3 }} />
            </Row>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
              <Column span={{ xs: 12, sm: 8, md: 6.5, lg: 4.5 }}>
                <View>
                  <Stack space="smaller">
                    <TextInput
                      label={translate("authStack.emailOrUsername") as string}
                      error={errors?.[UserFields.EMAIL]}
                      onChangeText={(val: string) => handleFieldUpdate(UserFields.EMAIL, val)}
                      returnKeyType="go"
                      keyboardType="email-address"
                      inputAccessoryViewID={inputAccessoryViewID}
                      ref={ref[0] as any}
                      onFocus={() => setCurrentInputIndex(0)}
                      onSubmitEditing={handleSubmit(handleForgotPassword)}
                      {...testProps("forgot-password-email-address")}
                    />
                  </Stack>
                </View>
                <View style={{ marginTop: 40 }}>
                  <Button
                    text={translate("authStack.sendResetLink") as string}
                    mode="contained"
                    loading={isLoading}
                    onPress={handleSubmit(handleForgotPassword)}
                    textStyle={{ fontFamily: "Lato_700Bold" }}
                    {...testProps("forgot-password-send-reset-link")}
                  />
                  {Platform.OS === "ios" ? (
                    <InputAccessoryNavigation
                      inputAccessoryViewID={inputAccessoryViewID}
                      ref={ref as any}
                      currentInputIndex={currentInputIndex}
                    />
                  ) : null}
                </View>
              </Column>
              <Column span={{ xs: false, sm: 2, md: 2.75, lg: 3.75 }} />
            </Row>
          </Container>
        </Grid>
      </ScrollView>
      <Footer />
    </KeyboardAvoidingView>
  );
};

const getStyles = (theme: ThemeType) => {
  const { colors, fontSize, spacing } = theme;
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: themeUiComponent.colors.white,
    },
    scrollView: {
      ...domusoResponsiveStyleHelper({
        webPhone: { flexGrow: 0 },
        webTablet: { flexGrow: 0 },
        webDesktop: { flexGrow: 0 },
        iosPhone: { flex: 1 },
        iosTablet: { flex: 1 },
        androidPhone: { flex: 1 },
        androidTablet: { flex: 1 },
      }),
    },
    scrollableArea: {},
    inner: {},
    text: {
      ...typography.title2,
      color: colors.text,
      fontWeight: "400",
    },
    heading: {
      fontSize: fontSize.largerPlus,
      fontWeight: "bold",
    },
    subHeading: {
      fontSize: 16,
      textAlign: "center",
      color: colors.text,
      fontWeight: "400",
    },
    bottomButton: {
      ...domusoResponsiveStyleHelper({
        webPhone: { outlineStyle: "none" },
        webTablet: { outlineStyle: "none" },
        webDesktop: { outlineStyle: "none" },
        iosPhone: { flex: 1 },
        iosTablet: { flex: 1 },
        androidPhone: { flex: 1 },
        androidTablet: { flex: 1 },
      }),
    },
  });
};
